import { Box, BoxProps } from '@chakra-ui/react';
import { useMultiStyleConfig } from '@chakra-ui/system';
import { mergeWith } from '@chakra-ui/utils';
import PropTypes from 'prop-types';
import React from 'react';
import ReactDatePicker from 'react-datepicker';

import 'react-datepicker/dist/react-datepicker.css';
import DatePickerHeader from './_DatePickerHeader';
import useDatePicker from './_useDatePicker';

import IconButton from 'atoms/Button/IconButton';
import Text from 'atoms/Typography/Text';
import Input from 'molecules/Input/Input';

const formatLongDate = (date: Date) => {
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const isInvalidDate = (date: Date) => {
  return Number.isNaN(date.getTime());
};

const range = (start: number, end: number) => {
  const array = [];
  for (let i = start; i <= end; i += 1) {
    array.push(i);
  }

  return array;
};

// year options for select, from 80 years before to 10 years from now
const defaultYearSelectOptions = range(new Date().getFullYear() - 80, new Date().getFullYear() + 10);

const propTypes = {
  onChange: PropTypes.func,
  selected: PropTypes.instanceOf(Date),
  rangeSelected: PropTypes.shape({ start: PropTypes.instanceOf(Date), end: PropTypes.instanceOf(Date) }),
  onRangeChange: PropTypes.func,
  excludeDates: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
  excludeDateIntervals: PropTypes.arrayOf(
    PropTypes.shape({ start: PropTypes.instanceOf(Date), end: PropTypes.instanceOf(Date) })
  ),
  filterDate: PropTypes.func,
  size: PropTypes.string,
  isDisabled: PropTypes.bool,
  isRange: PropTypes.bool,
  disableMonthYearSelector: PropTypes.bool,
  yearSelectOptions: PropTypes.arrayOf(PropTypes.number),
  _isOpen: PropTypes.bool
};

const defaultProps = {
  onChange: undefined,
  selected: undefined,
  rangeSelected: undefined,
  onRangeChange: undefined,
  excludeDates: undefined,
  excludeDateIntervals: undefined,
  filterDate: undefined,
  size: 'sm',
  isDisabled: false,
  isRange: false,
  disableMonthYearSelector: false,
  yearSelectOptions: defaultYearSelectOptions,
  _isOpen: false
};

export type DateRange = {
  start: Date | undefined;
  end: Date | undefined;
};

type DateExcludeRange = {
  start: Date;
  end: Date;
};

export type DatePickerProps = Omit<BoxProps, 'onChange' | 'onSelect'> & {
  onChange?: (date: Date | undefined) => void;
  selected?: Date;
  rangeSelected?: DateRange;
  onRangeChange?: (date: DateRange | undefined) => void;
  excludeDates?: Date[];
  excludeDateIntervals?: DateExcludeRange[];
  filterDate?: (date: Date) => boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  isDisabled?: boolean;
  isRange?: boolean;
  disableMonthYearSelector: boolean;
  yearSelectOptions: number[];
  _isOpen?: boolean;
};

const DatePicker = (props: DatePickerProps): React.ReactElement => {
  const {
    __css,
    onChange,
    selected,
    rangeSelected,
    onRangeChange,
    excludeDates,
    excludeDateIntervals,
    filterDate,
    size,
    placeholder,
    isDisabled,
    isRange,
    disableMonthYearSelector,
    yearSelectOptions,
    _isOpen,
    ...rest
  } = props;
  const {
    isOpen,
    isRangeInputFocused,
    setIsRangeInputFocused,
    setInputRef,
    setInputRangeStartRef,
    setInputRangeEndRef,
    boxRef,
    buttonRef,
    footerRef,
    buttonProps,
    disclosureProps,
    toogleFocusTodayDate,
    handleClickCalendarButton,
    onInputChange,
    onInputRangeStartChange,
    onInputRangeEndChange,
    onCalendarChange,
    getInputValue,
    getInputRangeStartValue,
    getInputRangeEndValue,
    onClickOutside,
    handleKeyDown
  } = useDatePicker({ onChange, selected, rangeSelected, onRangeChange, isRange });

  const DatePickerTheme = useMultiStyleConfig('DatePickerTheme', { size });
  const InputTheme = useMultiStyleConfig('Input', { size });

  const getCalendarButtonLabel = () => {
    if (isRange) {
      return rangeSelected
        ? `Choose range date${
            rangeSelected.start ? `, selected starts in ${formatLongDate(rangeSelected.start)}` : ''
          }${rangeSelected.end ? ` and ends in ${formatLongDate(rangeSelected.end)}` : ''}`
        : 'Choose range date';
    }

    return selected ? `Choose date, selected is ${formatLongDate(selected)}` : 'Choose date';
  };

  const getDayClassName = (date: Date) => {
    if (selected && formatLongDate(date) === formatLongDate(selected)) {
      return 'day--is-selected';
    }

    if (
      (rangeSelected?.start && formatLongDate(date) === formatLongDate(rangeSelected.start)) ||
      (rangeSelected?.end && formatLongDate(date) === formatLongDate(rangeSelected.end))
    ) {
      return 'day--is-selected';
    }

    return '';
  };

  const format = !isRange ? '##/##/####' : '##/##/#### – ##/##/####';
  const defaultPlaceholder = isRange ? 'MM/DD/YYYY – MM/DD/YYYY' : 'MM/DD/YYYY';
  const placeholderProp = placeholder === undefined ? defaultPlaceholder : placeholder;

  return (
    <Box __css={mergeWith(DatePickerTheme, __css)} ref={boxRef} {...rest}>
      {!isRange && (
        <Input
          customOptions={{ format }}
          data-focus={isOpen || undefined}
          getInputRef={setInputRef}
          isDisabled={isDisabled}
          mask="custom"
          onChange={onInputChange}
          placeholder={placeholderProp}
          size={size}
          value={getInputValue()}
        />
      )}
      {isRange && (
        <Box
          data-focus={isOpen || isRangeInputFocused || undefined}
          sx={mergeWith(InputTheme.field, DatePickerTheme.rangeInput)}
        >
          <Input
            aria-label="Start date"
            customOptions={{ format: '##/##/####' }}
            getInputRef={setInputRangeStartRef}
            mask="custom"
            onBlur={() => setIsRangeInputFocused(false)}
            onChange={onInputRangeStartChange}
            onFocusCapture={() => setIsRangeInputFocused(true)}
            placeholder="MM/DD/YYYY"
            size={size}
            sx={DatePickerTheme.rangeInputStart}
            value={getInputRangeStartValue()}
            variant="unstyled"
          />
          <Text sx={DatePickerTheme.rangeInputDivider}>–</Text>
          <Input
            aria-label="End date"
            customOptions={{ format: '##/##/####' }}
            getInputRef={setInputRangeEndRef}
            mask="custom"
            onBlur={() => setIsRangeInputFocused(false)}
            onChange={onInputRangeEndChange}
            onFocusCapture={() => setIsRangeInputFocused(true)}
            placeholder="MM/DD/YYYY"
            size={size}
            sx={DatePickerTheme.rangeInputEnd}
            value={getInputRangeEndValue()}
            variant="unstyled"
          />
        </Box>
      )}
      {(isOpen || _isOpen) && (
        // handleKeyDown for tab navigation inside the container
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <div onKeyDown={handleKeyDown} role="dialog">
          <ReactDatePicker
            {...disclosureProps}
            className="react-datapicker__input-text"
            customTimeInput={<Box ref={footerRef} />} // there isn't an api to custom render the today button or footer, so using the time input instead
            dayClassName={getDayClassName}
            endDate={rangeSelected?.end && !isInvalidDate(rangeSelected?.end) ? rangeSelected?.end : undefined}
            excludeDateIntervals={excludeDateIntervals}
            excludeDates={excludeDates}
            filterDate={filterDate}
            inline
            onChange={onCalendarChange}
            onClickOutside={onClickOutside}
            renderCustomHeader={headerProps => (
              <DatePickerHeader
                disableMonthYearSelector={disableMonthYearSelector}
                focusTodayButton={toogleFocusTodayDate}
                footerRef={footerRef}
                yearSelectOptions={yearSelectOptions}
                {...headerProps}
              />
            )}
            selected={selected && !isInvalidDate(selected) ? selected : undefined}
            selectsRange={isRange}
            showPopperArrow={false}
            showTimeInput
            startDate={rangeSelected?.start && !isInvalidDate(rangeSelected?.start) ? rangeSelected?.start : undefined}
            {...rest}
          />
        </div>
      )}
      <IconButton
        {...buttonProps}
        aria-label={getCalendarButtonLabel()}
        disabled={isDisabled}
        onClick={handleClickCalendarButton}
        ref={buttonRef}
        size={size}
        sx={DatePickerTheme.calendarButton}
        type="Calendar"
        variant="ghost"
      />
    </Box>
  );
};

DatePicker.propTypes = propTypes;
DatePicker.defaultProps = defaultProps;

export default DatePicker;
